import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { db } from '../lib/firebase';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import {
  Users, UserPlus, Phone, Mail, Clock, Trash2, Pencil, Search, X, Loader2, Plus
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AdminCustomersTab = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);

  // Real-time listener
  useEffect(() => {
    const q = query(collection(db, 'customers'), orderBy('created_at', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error('Firestore listener error:', err);
    });
    return unsub;
  }, []);

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.includes(q);
  });

  const resetForm = () => { setForm({ name: '', phone: '', email: '' }); setEditId(null); setShowForm(false); };

  const handleEdit = (c) => { setForm({ name: c.name, phone: c.phone, email: c.email }); setEditId(c.id); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/firestore/customers/${editId}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/firestore/customers`, form, { withCredentials: true });
      }
      resetForm();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/firestore/customers/${id}`, { withCredentials: true });
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-blue/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-brand-blue" />
          </div>
          <div>
            <h2 className="font-heading text-xl text-white">Customers</h2>
            <p className="text-slate-500 text-xs">{customers.length} total &middot; Firestore real-time</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-brand-surface border border-white/10 rounded-lg text-white placeholder-slate-600 text-sm focus:border-brand-red outline-none w-48"
              data-testid="customer-search" />
          </div>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-brand-red hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="add-customer-btn">
            <Plus className="w-4 h-4" /> Add Customer
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-brand-surface rounded-xl border border-white/10 p-6 mb-6" data-testid="customer-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-white">{editId ? 'Edit Customer' : 'New Customer'}</h3>
            <button onClick={resetForm} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="grid sm:grid-cols-3 gap-4">
            <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" required
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none text-sm" data-testid="customer-name-input" />
            <input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" required
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none text-sm" data-testid="customer-phone-input" />
            <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" required
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none text-sm" data-testid="customer-email-input" />
            <div className="sm:col-span-3 flex justify-end">
              <button type="submit" disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-brand-red hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors" data-testid="customer-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                {editId ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-20"><Users className="w-12 h-12 text-slate-600 mx-auto mb-4" /><p className="text-slate-500">No customers found</p></div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              className="bg-brand-surface rounded-xl border border-white/10 p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`customer-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium">{c.name}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm mt-1">
                  <span className="text-slate-400 flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>
                  <span className="text-slate-400 flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>
                  <span className="text-slate-600 flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(c.created_at)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => handleEdit(c)} className="p-2 rounded-lg bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 transition-colors" data-testid={`edit-customer-${c.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" data-testid={`delete-customer-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-brand-navy border-white/10">
                    <AlertDialogHeader><AlertDialogTitle className="text-white">Delete Customer</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">Permanently delete {c.name}?</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-white/5 text-white border-white/10 hover:bg-white/10">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(c.id)} className="bg-red-600 text-white hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCustomersTab;
